import React, { useState } from "react"
import { Button, Select, Tooltip } from "antd"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faPalette } from "@fortawesome/free-solid-svg-icons"
import TableStyleDrawer, { Swatch } from "./TableStyleDrawer.component"
import { describeTableStyle, LOOK_PRESETS } from "shared/ui/Table/style/looks"
import {
  DEFAULT_TABLE_STYLE_CHOICE,
  PRESET_IDS,
  PresetId,
  TableStyleChoice,
} from "types/tableStyle.types"

/** Props of the look picker with its Customise button (the report and its tabs). */
export interface TableStyleControlProps {
  /** The choice being edited; undefined is the default, or "inherit" with `allowInherit`. */
  value: TableStyleChoice | undefined
  /**
   * Called with each new choice: a picked look (without tweaks), undefined
   * for the inherit option, or a change made in the drawer.
   */
  onChange: (value: TableStyleChoice | undefined) => void
  /** Adds the inherit option (a report tab following the report style). */
  allowInherit?: boolean
  /**
   * Names the inherit option, before the look it resolves to; default
   * "Inherit report". A dashboard tile uses "Dashboard default".
   */
  inheritLabel?: string
  /** What an inheriting value resolves to (the report style). */
  inherited?: TableStyleChoice
  /** Title of the Customise drawer, naming its scope. */
  drawerTitle: string
  /** antd size of the picker and the button; default "small". */
  size?: "small" | "middle"
}

const INHERIT = "inherit"

/**
 * Picks a table look and opens the Customise drawer. The drawer edits the
 * same value; for an inheriting tab it starts from the report's style and
 * the first change creates the tab's own.
 */
const TableStyleControl: React.FC<TableStyleControlProps> = ({
  value,
  onChange,
  allowInherit = false,
  inheritLabel = "Inherit report",
  inherited,
  drawerTitle,
  size = "small",
}) => {
  const [open, setOpen] = useState(false)
  const fallback = allowInherit
    ? (inherited ?? DEFAULT_TABLE_STYLE_CHOICE)
    : DEFAULT_TABLE_STYLE_CHOICE
  const shown = value ?? fallback
  const selected = allowInherit && value === undefined ? INHERIT : shown.preset
  const options = [
    ...(allowInherit
      ? [{ value: INHERIT, label: `${inheritLabel} (${describeTableStyle(fallback)})` }]
      : []),
    ...PRESET_IDS.map((id) => ({
      value: id,
      label: (
        <span>
          <Swatch color={LOOK_PRESETS[id].swatch} />
          {id === selected ? describeTableStyle(shown) : LOOK_PRESETS[id].label}
        </span>
      ),
    })),
  ]
  return (
    <div className="flex items-center gap-2">
      <Select
        size={size}
        aria-label="Table style"
        className="min-w-[190px]"
        value={selected}
        options={options}
        onChange={(v: string) =>
          onChange(v === INHERIT ? undefined : { preset: v as PresetId })
        }
      />
      <Tooltip title="Customise colours, font, lines and numbers">
        <Button
          size={size}
          icon={<FontAwesomeIcon icon={faPalette} />}
          aria-label="Customise table style"
          onClick={() => setOpen(true)}
        >
          Customise
        </Button>
      </Tooltip>
      <TableStyleDrawer
        open={open}
        title={drawerTitle}
        value={value}
        fallback={fallback}
        onChange={onChange}
        onClose={() => setOpen(false)}
      />
    </div>
  )
}

export default TableStyleControl
